import type { Collection } from '../domain/collections';
import { useLoad } from './useLoad';
import { useRepositories } from './useRepositories';

/** The owned collections a page last loaded, per repository, for when a later load fails. */
const lastKnownOwned = new WeakMap<object, Collection[]>();

/** Every collection I can see — mine, ones I'm a member of, and followed owners' open ones. */
export function useCollections() {
  const { collections } = useRepositories();
  return useLoad<Collection[]>(async () => {
    const all = await collections.list();
    lastKnownOwned.set(
      collections,
      all.filter((c) => c.myRelation === 'owner'),
    );
    return all;
  }, 'collections');
}

export function useCollection(id: string) {
  const { collections } = useRepositories();
  return useLoad<Collection>(() => collections.get(id), id);
}

export type OwnedCollections = {
  collections: Collection[];
  /** Loaded earlier on this page, because this load failed (021 R13). */
  remembered: boolean;
};

/**
 * Collections I own, for choosing where new drafts go. When the load fails
 * (offline), it offers the ones this page loaded last (021 FR-003a, R13);
 * with none known, it fails.
 */
export function useOwnedCollections() {
  const { collections } = useRepositories();
  return useLoad<OwnedCollections>(async () => {
    try {
      const mine = (await collections.list()).filter((c) => c.myRelation === 'owner');
      lastKnownOwned.set(collections, mine);
      return { collections: mine, remembered: false };
    } catch (error) {
      const known = lastKnownOwned.get(collections);
      if (known) return { collections: known, remembered: true };
      throw error;
    }
  }, 'owned-collections');
}

/** My private collections — the ones a group link can offer. */
export function offerable(collections: Collection[]): Collection[] {
  return collections.filter((c) => c.myRelation === 'owner' && c.visibility === 'private');
}
